import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatValueMap, getChartMetricsOptions } from '../../../constants';
import { dealwithKeyMetricsLine } from '../../../utils';
import { getInfluencerCreatorTimeline } from '@op/services';
import LineChart from '../../components/LineChart';
import { formatDateFunc } from '@op/utils/time';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { Justify, Select } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import { arrayToSimpleObj } from '@op/utils';
import { dealwithBiLiBiLiImage } from '@op/utils/cdnUrl';
import CardCommon from '@op/components/CardCommon';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { barIcon, lineIcon } from '@op/constants/chart';
import { TableColorType } from '@op/hooks/useTableSelect';
import './index.scss';

const metricMap: any = {
  peak_ccv: 'peakCCV',
  avg_ccv: 'avgCCV',
  hours_watched: 'hoursWatched',
};

export default function InfluencerMetrics({
  params,
  mediaType,
  selectedKeysMap,
  tableLoading,
}: {
  mediaType: string;
  params: any;
  selectedKeysMap: TableColorType;
  tableLoading: boolean;
}) {
  const [serversData, setServersData] = useState({});
  const [lineLoading, setLineLoading] = useState(true);
  const [metric, setMetric] = useState('views');
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  const { downloadLegacy, downloading } = useDownloadParams();

  const formatValueFunc = useCallback(
    (value: number) => {
      return formatValueMap[metric](value);
    },
    [metric],
  );

  const { formatMessage, formatOptionsMessage } = useLanguage();

  const { options, optionsMap } = useMemo(() => {
    const options = formatOptionsMessage(getChartMetricsOptions(mediaType));
    const optionsMap = arrayToSimpleObj(options);
    return {
      options,
      optionsMap,
    };
  }, [mediaType, formatOptionsMessage]);

  const [colorObj, userIds, sortObj] = useMemo(() => {
    const colorInfo: any = {};
    const sortInfo: any = {};
    const Ids: Array<string> = [];
    for (const key in selectedKeysMap) {
      const element = selectedKeysMap[key] || {};
      colorInfo[key] = element.color;
      sortInfo[key] = element.sort;
      Ids.push(element.user_id);
    }
    return [colorInfo, Ids, sortInfo];
  }, [selectedKeysMap]);
  const [lineSeriesData, lineXAxisData] = useMemo(() => {
    let localSeriesData: Array<any> = [];
    let localXAxisData: Array<any> = [];
    if (Object.keys(serversData).length > 0) {
      const { timeline_of_start_time, timeline_of_end_time, timeline } =
        serversData as any;
      const [lineSeriesData, lineXAxisData] = dealwithKeyMetricsLine(
        timeline_of_start_time,
        timeline_of_end_time,
        timeline?.map((item: any) => ({
          ...item,
          name: item.key,
          count: item.value,
          metric,
        })),
        userIds,
      );
      localSeriesData = lineSeriesData;
      localXAxisData = lineXAxisData;
    }
    return [localSeriesData, localXAxisData];
  }, [serversData, userIds, mediaType]);

  const getTimelineData = (value = metric) => {
    if (Object.keys(params || {}).length !== 0 && userIds.length > 0) {
      setLineLoading(true);
      getInfluencerCreatorTimeline({
        ...params,
        metrics: [metricMap[value] || value],
        anchor_md5s: userIds,
      })
        .then((data: any) => {
          setServersData(data || {});
        })
        .finally(() => {
          setLineLoading(false);
        });
    }
  };

  useEffect(() => {
    setServersData({});
  }, [mediaType, metric, userIds]);

  useEffect(() => {
    getTimelineData();
  }, [userIds]);

  useEffect(() => {
    setMetric(mediaType === 'stream' ? 'peak_ccv' : 'views');
  }, [mediaType]);

  const formatDate = formatDateFunc(params.date_type);

  const download = () => {
    downloadLegacy({
      params: {
        ...params,
        metrics: [metricMap[metric] || metric],
        anchor_md5s: userIds,
      },
      simpleName: 'Compare By',
      downloadFunc: 'getInfluencerCreatorTimeline',
      sheetName: 'Compare By Influencer',
    });
  };

  return (
    <CardCommon
      hasDownload
      downloadLoading={downloading}
      operations={[barIcon, lineIcon]}
      handleDownloadOnClick={download}
      defaultOperation="bar"
      className="influencer-metrics-wrapper"
      title={formatMessage({ id: 'Compare by' })}
      handleOperationsOnClick={(value: any) => {
        setChartType(value.symbol);
      }}
    >
      <Justify
        left={
          <Select
            className="select-metric-box"
            value={metric}
            matchButtonWidth
            onChange={(value) => {
              setMetric(value);
              getTimelineData(value);
            }}
            appearance="button"
            options={options}
          ></Select>
        }
      ></Justify>
      <LineChart
        height={350}
        connectNulls={false}
        seriesDataArray={lineSeriesData}
        loading={lineLoading || tableLoading}
        title={optionsMap[metric]}
        colorObj={colorObj}
        sortObj={sortObj}
        isStack={chartType === 'bar'}
        lineLegendFormatter={(name: string) => {
          const { name: userName, channel } = selectedKeysMap[name] || { name };
          return `{${channel}| } ${userName}`;
        }}
        tooltipFormatterFunc={(_param: any) => {
          const rightTitle = optionsMap[metric];
          const letTitle = formatDate(_param[0]?.axisValue);

          return formatChartParamsDate({
            params: _param,
            textShowFun: (marker: string, seriesName: string) => {
              let key = seriesName;
              const { name, icon } = selectedKeysMap[key] || {};
              const img = dealwithBiLiBiLiImage(icon);
              return `<span class="chart-common-formatter-marker">${marker}</span>&nbsp;
                      <span class="icon-default-img"><span style="background-image: url(${img});" class="icon-img"></span></span>
                      ${name || key}`;
            },
            isSort: true,
            valueFormat: formatValueFunc,
            tablesConfigFunc: (records) => {
              return [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: letTitle,
                    },
                    {
                      key: 'value',
                      header: rightTitle,
                      align: 'right',
                      weight: 'bold',
                    },
                  ],
                },
              ];
            },
          });
        }}
        xAxisData={lineXAxisData}
        dateType={params.date_type}
        chartType={chartType}
      ></LineChart>
    </CardCommon>
  );
}
